import { Component, forwardRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { FloatLabelType, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'lib-cdw-number-input',
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule],
  templateUrl: './common-ui-components-number-input.component.html',
  styleUrl: './common-ui-components-number-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CommonUiComponentsNumberInputComponent),
      multi: true,
    },
  ],
})
export class CommonUiComponentsNumberInputComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() required = false;
  @Input() hideRequiredMarker = false;
  @Input() floatLabel!: FloatLabelType;
  @Input() size?: string; // Accepts percentage (e.g., '100%') or px (e.g., '400px')
  @Input() min?: number;
  @Input() max?: number;
  @Input() step = 1;
  @Input() disableLastPass = true;

  value: number | null = null;

  onChange = (value: number | null) => {
    // Callback placeholder - will be replaced by Angular's ControlValueAccessor
  };

  onTouched = () => {
    // Callback placeholder - will be replaced by Angular's ControlValueAccessor
  };

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: number | null): void {
    this.value = value;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChanged(value: string): void {
    const numValue = value === '' ? null : Number(value);
    this.value = numValue;
    this.onChange(numValue);
    this.onTouched();
  }

  increment(): void {
    if (this.disabled || this.readonly) return;

    const currentValue = this.value ?? 0;
    let newValue = currentValue + this.step;

    if (this.max !== undefined && newValue > this.max) {
      newValue = this.max;
    }

    this.value = newValue;
    this.onChange(newValue);
    this.onTouched();
  }

  decrement(): void {
    if (this.disabled || this.readonly) return;

    const currentValue = this.value ?? 0;
    let newValue = currentValue - this.step;

    if (this.min !== undefined && newValue < this.min) {
      newValue = this.min;
    }

    this.value = newValue;
    this.onChange(newValue);
    this.onTouched();
  }
}
